/**
 * ShiftFormPage - Página de criação/edição de turno
 *
 * Seções:
 * 1. Informações Básicas (nome, descrição, equipe)
 * 2. Membros do Turno (disponível em criação e edição)
 * 3. Configuração de Horário (tipo, dias, horários)
 */

import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useShift, useCreateShift, useUpdateShift, useAddShiftMember } from '../hooks';
import { useTeamsDropdown } from '@/modules/teams/hooks';
import { Button } from '@/shared/components/ui/Button';
import { Breadcrumb, FormSection, Select, Input, Textarea, Card, CardContent } from '@/shared/components/ui';
import { ShiftMembersSection } from '../components';
import { ContextUsersTab } from '@/shared/components/context-users/ContextUsersTab';
import type { CreateShiftRequest, UpdateShiftRequest, ShiftSchedule } from '@troia-v3/types';
import { Icon } from '@/shared/icons'
const WEEKDAYS = [
  { value: 1, label: 'Seg' },
  { value: 2, label: 'Ter' },
  { value: 3, label: 'Qua' },
  { value: 4, label: 'Qui' },
  { value: 5, label: 'Sex' },
  { value: 6, label: 'Sáb' },
  { value: 0, label: 'Dom' },
];

// Schedule type simplification: always fixed. SCHEDULE_TYPES selector removed.

const TRANSITION_STRATEGIES = [
  { value: 'immediate', label: 'Imediata', description: 'Troca imediata de turno', icon: 'zap' as const },
  { value: 'finish_current', label: 'Finalizar Atual', description: 'Aguardar finalização das atividades', icon: 'check-circle' as const },
  { value: 'overlap', label: 'Sobreposição', description: 'Período de transição entre turnos', icon: 'layers' as const },
];

interface ShiftFormData {
  name: string;
  description: string;
  teamId: string;
  weekdays: number[];
  startTime: string;
  endTime: string;
  timezone: string;
  overlapMinutes: number;
  transitionStrategy: 'immediate' | 'finish_current' | 'overlap';
}

const getDefaultFormData = (): ShiftFormData => ({
  name: '',
  description: '',
  teamId: '',
  weekdays: [1, 2, 3, 4, 5], // Segunda a Sexta por padrão
  startTime: '08:00',
  endTime: '18:00',
  timezone: 'America/Sao_Paulo',
  overlapMinutes: 15,
  transitionStrategy: 'immediate',
});

export const ShiftFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditMode = !!id;

  // Fetch shift data (only in edit mode)
  const { data: shift, isLoading, isError } = useShift(id);
  const createMutation = useCreateShift();
  const updateMutation = useUpdateShift();

  // Fetch teams for dropdown
  const { data: teams = [] } = useTeamsDropdown();

  // Add member mutation (used after shift creation)
  const addMemberMutation = useAddShiftMember();

  // Form state
  const [formData, setFormData] = useState<ShiftFormData>(getDefaultFormData());
  const [hasChanges, setHasChanges] = useState(!isEditMode);

  // Pending members for create mode (stored locally until shift is created)
  const [pendingMembers, setPendingMembers] = useState<Array<{
    id: string;
    name: string;
    avatar?: string;
  }>>([]);

  // Update form data when shift loads (edit mode)
  React.useEffect(() => {
    if (shift) {
      // Support both new flat shape and legacy nested `fixedSchedule`
      const sched = (shift.schedule as any)?.fixedSchedule ?? shift.schedule;
      setFormData({
        name: shift.name || '',
        description: shift.description || '',
        teamId: shift.teamId?.toString() || '',
        weekdays: sched?.weekdays || [1, 2, 3, 4, 5],
        startTime: sched?.startTime || '08:00',
        endTime: sched?.endTime || '18:00',
        timezone: sched?.timezone || 'America/Sao_Paulo',
        overlapMinutes: shift.schedule?.overlapMinutes ?? 15,
        transitionStrategy: shift.schedule?.transitionStrategy || 'immediate',
      });
      setHasChanges(false);
    }
  }, [shift]);

  // Handle form field changes
  const handleFieldChange = (field: keyof ShiftFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  // Handle weekday toggle
  const toggleWeekday = (day: number) => {
    setFormData(prev => {
      const newWeekdays = prev.weekdays.includes(day)
        ? prev.weekdays.filter(d => d !== day)
        : [...prev.weekdays, day];
      return { ...prev, weekdays: newWeekdays };
    });
    setHasChanges(true);
  };

  // Handle pending members change (for create mode)
  const handlePendingMembersChange = (members: Array<{ id: string; name: string; avatar?: string }>) => {
    setPendingMembers(members);
    setHasChanges(true);
  };

  // Build schedule object (always fixed — no type/fixedSchedule wrapper)
  const buildSchedule = (): ShiftSchedule => ({
    weekdays: formData.weekdays,
    startTime: formData.startTime,
    endTime: formData.endTime,
    timezone: formData.timezone,
    overlapMinutes: formData.overlapMinutes,
    transitionStrategy: formData.transitionStrategy,
  });

  // Validation
  const validateForm = (): string | null => {
    if (!formData.name?.trim()) return 'Nome é obrigatório';
    if (formData.name.trim().length < 2) return 'Nome deve ter pelo menos 2 caracteres';
    if (formData.weekdays.length === 0) {
      return 'Selecione pelo menos um dia da semana';
    }
    if (!formData.startTime) {
      return 'Horário de início é obrigatório';
    }
    if (!formData.endTime) {
      return 'Horário de término é obrigatório';
    }
    return null;
  };

  // Save changes
  const handleSave = async () => {
    if (!hasChanges) return;

    const validationError = validateForm();
    if (validationError) {
      alert(validationError);
      return;
    }

    try {
      const schedule = buildSchedule();

      if (isEditMode) {
        const updateData: UpdateShiftRequest = {
          name: formData.name,
          description: formData.description || undefined,
          teamId: formData.teamId || undefined,
          schedule,
        };
        await updateMutation.mutateAsync({ id: id!, data: updateData });
        setHasChanges(false);
        navigate('/settings/shifts');
      } else {
        const createData: CreateShiftRequest = {
          name: formData.name,
          description: formData.description || undefined,
          teamId: formData.teamId || undefined,
          schedule,
          assignmentConfig: {
            enabled: true,
            type: 'sequential',
          },
        };
        const newShift = await createMutation.mutateAsync(createData);

        // Add pending members after shift creation
        if (pendingMembers.length > 0 && newShift?.id) {
          for (const member of pendingMembers) {
            try {
              await addMemberMutation.mutateAsync({
                shiftId: newShift.id,
                data: { userId: member.id, role: 'primary' }
              });
            } catch (error) {
              console.error('Erro ao adicionar membro:', error);
            }
          }
        }

        navigate('/settings/shifts');
      }
    } catch (error: any) {
      console.error('Erro ao salvar turno:', error);
      alert('Erro ao salvar turno. Tente novamente.');
    }
  };

  // Loading state
  if (isEditMode && isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Icon name="loader" className="w-8 h-8 animate-spin" style={{ color: 'var(--theme-button-primary)' }} />
      </div>
    );
  }

  // Error state
  if (isEditMode && (isError || !shift)) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4">
        <p className="text-lg" style={{ color: 'var(--theme-error-color)' }}>
          Erro ao carregar turno
        </p>
        <Button onClick={() => window.location.reload()} variant="secondary">
          Tentar Novamente
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--theme-page-background)' }}>
      {/* Header */}
      <div
        className="sticky top-0 z-10"
        style={{
          backgroundColor: 'var(--theme-card-background)',
          borderBottom: '1px solid var(--theme-card-border)',
        }}
      >
        <div className="px-4 h-14 flex flex-col justify-center">
          <div className="flex items-center justify-between">
            {/* Breadcrumb (F25-31: shared — mobile = legenda condensada sob o título, NUNCA seta voltar) */}
            <Breadcrumb
              items={[
                { label: 'Configurações', onNavigate: () => navigate('/settings'), testid: 'shifts-form-breadcrumb-settings' },
                { label: 'Turnos', onNavigate: () => navigate('/settings/shifts'), testid: 'shifts-form-breadcrumb' },
                { label: isEditMode ? (shift ? shift.name : 'Editar turno') : 'Novo turno', testid: 'shifts-form-breadcrumb-current' },
              ]}
              mobileLegend="Configurações · Turno"
              data-testid="shifts-form-breadcrumb-nav"
            />

            <div className="flex items-center gap-3">
              {hasChanges && (
                <span
                  className="text-sm hidden sm:inline"
                  style={{ color: 'var(--theme-warning-color)' }}
                >
                  {isEditMode ? 'Alterações não salvas' : 'Preencha os campos obrigatórios'}
                </span>
              )}
              <Button
                variant="secondary"
                className="hidden sm:inline-flex"
                onClick={() => navigate('/settings/shifts')}
                data-testid="shifts-form-cancel"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSave}
                disabled={!hasChanges || createMutation.isPending || updateMutation.isPending}
              >
                {createMutation.isPending || updateMutation.isPending ? (
                  <>
                    <Icon name="loader" className="w-4 h-4 mr-2 animate-spin" />
                    {isEditMode ? 'Salvando...' : 'Criando...'}
                  </>
                ) : (
                  <>
                    <Icon name="save" className="w-4 h-4 mr-2" />
                    {isEditMode ? 'Salvar Alterações' : 'Criar Turno'}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-8">
        <div className="space-y-6">
          {/* Basic Info Section */}
          <FormSection
            icon={'clock'}
            title="Informações Básicas"
            description="Nome, descrição e equipe do turno"
          >
            {/* Nome */}
            <div>
              <label
                className="block text-sm font-medium mb-2"
                style={{ color: 'var(--theme-card-text)' }}
              >
                Nome do Turno *
              </label>
              <Input
                type="text"
                value={formData.name}
                onChange={(e) => handleFieldChange('name', e.target.value)}
                placeholder="Ex: Turno Manhã, Turno Noturno"
                data-testid="shift-form-name"
              />
            </div>

            {/* Descrição */}
            <div>
              <label
                className="block text-sm font-medium mb-2"
                style={{ color: 'var(--theme-card-text)' }}
              >
                Descrição
              </label>
              <Textarea
                value={formData.description}
                onChange={(e) => handleFieldChange('description', e.target.value)}
                placeholder="Descreva as características deste turno"
                rows={3}
                resize="none"
                data-testid="shift-form-description"
              />
            </div>

            {/* Equipe */}
            <Select
              label="Equipe (opcional)"
              description="Vincule este turno a uma equipe específica"
              value={formData.teamId}
              onChange={(value) => handleFieldChange('teamId', value)}
              placeholder="Nenhuma equipe selecionada"
              options={teams.map((team) => ({
                value: team.id,
                label: team.name,
              }))}
            />
          </FormSection>

          {/* Members Section */}
          {isEditMode && id ? (
            <Card>
              <CardContent className="px-4 pb-4 pt-4">
                <ContextUsersTab
                  contextRef={{ kind: 'shift', id }}
                  hideRoleScope
                />
              </CardContent>
            </Card>
          ) : (
            <ShiftMembersSection
              shiftId={id}
              isCreateMode
              pendingMembers={pendingMembers}
              onPendingMembersChange={handlePendingMembersChange}
            />
          )}

          {/* Schedule Section */}
          <FormSection
            icon={'calendar'}
            title="Configuração de Horário"
            description="Horários e dias do turno"
          >
            <div className="space-y-6">
                  {/* Dias da Semana */}
                  <div>
                    <label
                      className="block text-sm font-medium mb-3"
                      style={{ color: 'var(--theme-card-text)' }}
                    >
                      Dias da Semana
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {WEEKDAYS.map((day) => (
                        <button
                          key={day.value}
                          type="button"
                          onClick={() => toggleWeekday(day.value)}
                          className="h-9 px-3 rounded-lg border text-sm font-medium transition-all"
                          style={{
                            backgroundColor: formData.weekdays.includes(day.value)
                              ? 'var(--theme-primary)'
                              : 'var(--theme-input-background)',
                            borderColor: formData.weekdays.includes(day.value)
                              ? 'var(--theme-primary)'
                              : 'var(--theme-input-border)',
                            color: formData.weekdays.includes(day.value)
                              ? '#ffffff'
                              : 'var(--theme-card-text)',
                          }}
                        >
                          {day.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Horários */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label
                        className="block text-sm font-medium mb-2"
                        style={{ color: 'var(--theme-card-text)' }}
                      >
                        Horário de Início *
                      </label>
                      <Input
                        type="time"
                        value={formData.startTime}
                        onChange={(e) => handleFieldChange('startTime', e.target.value)}
                        data-testid="shift-form-start-time"
                      />
                    </div>
                    <div>
                      <label
                        className="block text-sm font-medium mb-2"
                        style={{ color: 'var(--theme-card-text)' }}
                      >
                        Horário de Término *
                      </label>
                      <Input
                        type="time"
                        value={formData.endTime}
                        onChange={(e) => handleFieldChange('endTime', e.target.value)}
                        data-testid="shift-form-end-time"
                      />
                    </div>
                  </div>
              {/* Transition Strategy */}
              <div>
                <label
                  className="block text-sm font-medium mb-3"
                  style={{ color: 'var(--theme-card-text)' }}
                >
                  Estratégia de Transição
                </label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {TRANSITION_STRATEGIES.map((strategy) => (
                    <button
                      key={strategy.value}
                      type="button"
                      onClick={() => handleFieldChange('transitionStrategy', strategy.value)}
                      className="p-3 rounded-lg border text-left transition-all"
                      style={{
                        backgroundColor: formData.transitionStrategy === strategy.value
                          ? 'var(--theme-primary-background)'
                          : 'var(--theme-input-background)',
                        borderColor: formData.transitionStrategy === strategy.value
                          ? 'var(--theme-primary)'
                          : 'var(--theme-input-border)',
                      }}
                    >
                      <span
                        className="flex items-center gap-1.5 text-sm font-medium"
                        style={{
                          color: formData.transitionStrategy === strategy.value
                            ? 'var(--theme-primary)'
                            : 'var(--theme-card-text)',
                        }}
                      >
                        <Icon name={strategy.icon} className="w-4 h-4 flex-none" />
                        {strategy.label}
                      </span>
                      <span
                        className="block text-xs mt-1"
                        style={{ color: 'var(--theme-muted-text)' }}
                      >
                        {strategy.description}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Overlap Minutes */}
              {formData.transitionStrategy === 'overlap' && (
                <div>
                  <label
                    className="block text-sm font-medium mb-2"
                    style={{ color: 'var(--theme-card-text)' }}
                  >
                    Minutos de Sobreposição
                  </label>
                  <input
                    type="number"
                    value={formData.overlapMinutes}
                    onChange={(e) => handleFieldChange('overlapMinutes', parseInt(e.target.value) || 0)}
                    min={0}
                    max={60}
                    className="w-full max-w-xs h-[38px] px-4 py-2 rounded-lg border"
                    style={{
                      backgroundColor: 'var(--theme-input-background)',
                      borderColor: 'var(--theme-input-border)',
                      color: 'var(--theme-input-text)',
                    }}
                  />
                  <p className="mt-1 text-xs" style={{ color: 'var(--theme-muted-text)' }}>
                    Tempo de transição entre turnos (padrão: 15 minutos)
                  </p>
                </div>
              )}
            </div>
          </FormSection>
        </div>
      </div>
    </div>
  );
};

ShiftFormPage.displayName = 'ShiftFormPage';
